"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { usePathname, useRouter } from "next/navigation";

import "@/lib/i18n/chrome-bundles";
import { blogPathIn } from "@/app/(site)/[locale]/blog/data/paths";
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  isLocale,
  type Locale,
} from "@/lib/i18n/locale";

export type Language = Locale;

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

/** The page's scrollport — see app/(site)/[locale]/layout.tsx. */
const SCROLLER_ID = "scroll-container";

/**
 * Where the scroll offset waits out a language switch.
 *
 * `sessionStorage` rather than a ref or module variable because the switch
 * remounts this provider along with the rest of the `[locale]` tree, so
 * nothing held in React survives the trip.
 */
const SCROLL_RESTORE_KEY = "jd:language-scroll";

/**
 * How many frames the restore is allowed to keep re-applying itself. The RSC
 * payload arrives with the content already in it, so the first frame is
 * normally enough; the retries are for the case where a late-laying-out
 * section (an image finishing, a font swapping) grows the page under the
 * reader and shortens the scroll range for a frame or two.
 */
const RESTORE_FRAME_BUDGET = 20;

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

function readCookie(name: string) {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function writeCookie(locale: Locale) {
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
}

function rememberScroll() {
  const scroller = document.getElementById(SCROLLER_ID);
  if (!scroller) return;
  try {
    sessionStorage.setItem(SCROLL_RESTORE_KEY, String(scroller.scrollTop));
  } catch {
    // Storage can be blocked (private mode, a locked-down browser). The switch
    // still works; it just lands at the top of the page, as it used to.
  }
}

function takeRememberedScroll() {
  try {
    const stored = sessionStorage.getItem(SCROLL_RESTORE_KEY);
    if (stored === null) return null;
    // Read once: a later reload or a back/forward should start where the
    // browser puts it, not where the last language switch was.
    sessionStorage.removeItem(SCROLL_RESTORE_KEY);
    const top = Number(stored);
    return Number.isFinite(top) ? top : null;
  } catch {
    return null;
  }
}

/**
 * Puts the reader back where they were after a language switch.
 *
 * The switch remounts the `[locale]` tree, and the <div id="scroll-container">
 * that comes back is a brand-new element at the top of the page — which is why
 * changing language while reading the pricing table used to throw the reader
 * back to the hero. The offset is stashed just before the refresh and applied
 * here, on the mount that follows it.
 *
 * This runs in a layout effect so the jump happens in the same frame the new
 * tree is painted: the reader sees the page change language, not the page
 * change language and then scroll.
 */
function useRestoreScrollAfterSwitch(locale: Language) {
  useIsomorphicLayoutEffect(() => {
    const top = takeRememberedScroll();
    if (top === null || top <= 0) return;

    let frames = 0;
    let raf = 0;

    const apply = () => {
      const scroller = document.getElementById(SCROLLER_ID);
      // `scrollTo({ behavior: "instant" })` rather than assigning `scrollTop`:
      // the scrollport carries `scroll-behavior: smooth`, which would turn the
      // restore into a visible ride down the page.
      scroller?.scrollTo({ top, behavior: "instant" });

      const settled = scroller ? Math.abs(scroller.scrollTop - top) < 1 : false;
      if (!settled && frames++ < RESTORE_FRAME_BUDGET) {
        raf = requestAnimationFrame(apply);
      }
    };

    apply();
    return () => cancelAnimationFrame(raf);
  }, [locale]);
}

/**
 * Publishes the language the *server* rendered in, and owns changing it.
 *
 * This provider used to be where the language was decided: it started at Thai,
 * read localStorage in a layout effect and corrected itself. That correction
 * could not happen until React had hydrated, which meant every string on the
 * page was Thai until ~1 MB of JS had been parsed — 3.36 s of LCP render delay
 * for the English client Lighthouse emulates, and a visible flash for every
 * real English visitor.
 *
 * The decision now lives in the proxy, which reads the cookie and rewrites to a
 * prerendered tree that is already in the right language (lib/i18n/locale.ts).
 * `locale` here is that decision, handed down from the `[locale]` layout, so
 * the client components that still call `useTranslation` render the same
 * strings the server did and hydration is a no-op.
 */
export function LanguageProvider({
  locale,
  children,
}: Readonly<{ locale: Language; children: React.ReactNode }>) {
  const router = useRouter();
  const pathname = usePathname();

  useRestoreScrollAfterSwitch(locale);

  const setLanguage = useCallback(
    (next: Language) => {
      if (next === locale) return;
      writeCookie(next);
      // The strings live in the server-rendered payload now, so switching
      // language means asking the server for it again. `refresh()` re-fetches
      // the current URL's RSC payload — the proxy sees the new cookie and
      // rewrites to the other tree — without a full document load.
      //
      // Note what that costs: the `[locale]` segment changes value, so the
      // tree under it *remounts*. Every scroll entrance is armed again, and
      // anything holding a DOM node from the old tree is now holding a
      // detached one — which is why the scrollport is looked up through
      // `useScrollerRef` rather than captured once. The scroll offset is the
      // one part of that we hand back: stash it now, and the mount on the
      // other side puts the reader where they were (see
      // `useRestoreScrollAfterSwitch`).
      rememberScroll();
      // On the blog the language is part of the address (see proxy.ts), so a
      // refresh would come back in the same language — the other one lives at
      // another URL, and that is where the reader is going.
      const other = blogPathIn(pathname, next);
      if (other) router.push(other, { scroll: false });
      else router.refresh();
    },
    [locale, router, pathname],
  );

  // First-time visitors have no cookie, so the proxy served them Thai — see
  // DEFAULT_LOCALE for why that is deliberate rather than a guess. Detect a
  // non-Thai browser once, here, and record it; every later request is
  // server-rendered in English from the start. This is the only language work
  // left that costs a re-render, and it happens once per visitor ever. No
  // scroll to remember: this fires on the first paint of a visit, before the
  // reader has moved.
  useEffect(() => {
    if (readCookie(LOCALE_COOKIE)) return;
    const preferred: Language = navigator.language?.toLowerCase().startsWith("th")
      ? "th"
      : "en";
    writeCookie(preferred);
    // Not on the blog: its language comes from the URL, so a refresh there
    // would re-render the same page for nothing. And never navigate on the
    // reader's behalf — that would read as a redirect to a crawler that runs
    // scripts.
    if (preferred !== locale && !blogPathIn(pathname, locale)) router.refresh();
  }, [locale, router, pathname]);

  const value = useMemo(
    () => ({
      language: locale,
      setLanguage,
      toggleLanguage: () => setLanguage(locale === "en" ? "th" : "en"),
    }),
    [locale, setLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}

/**
 * Locale for trees rendered outside the public site's `[locale]` layout — the
 * CMS, which has no locale segment and is Thai-only.
 */
export function fallbackLocale(value?: string): Language {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}
